import { useState } from "react";
import { toast } from "sonner";
import { Users } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../i18n";
import { SpecialCta } from "../components/sections/CtaSections";

const INTERESTS = {
  hi: ["शिक्षा", "स्वास्थ्य", "महिला सशक्तिकरण", "पर्यावरण", "समाज सेवा", "जागरूकता अभियान", "अन्य"],
  en: ["Education", "Healthcare", "Women Empowerment", "Environment", "Social Service", "Awareness Campaigns", "Other"],
};

export default function Volunteer() {
  const { t, pick, lang } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", interest: "", contribution: "", message: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.name.trim().length < 2) errs.name = t("common.required");
    if (form.phone.trim().length < 8) errs.phone = t("common.required");
    if (form.city.trim().length < 2) errs.city = t("common.required");
    if (!form.interest) errs.interest = t("common.required");
    if (form.contribution.trim().length < 2) errs.contribution = t("common.required");
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await api.post("/volunteer", { ...form, email: form.email || null });
      toast.success(t("common.formSuccess"));
      setForm({ name: "", phone: "", email: "", city: "", interest: "", contribution: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err) || t("common.formError"));
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full rounded-xl border border-ngo-deep/20 bg-white px-4 py-3.5 text-sm text-ngo-ink placeholder:text-ngo-ink/40 focus:outline-none focus:ring-2 focus:ring-ngo-leaf/50 focus:border-ngo-leaf transition-[box-shadow,border-color] duration-200";
  const label = "block text-sm font-semibold text-ngo-ink mb-2";
  const err = "mt-1.5 text-xs text-red-600";

  return (
    <main>
      <Seo
        title="स्वयंसेवक बनें — हमसे जुड़ें"
        description="Anansha Welfare Society Varanasi में स्वयंसेवक बनें — शिक्षा, स्वास्थ्य, पर्यावरण और महिला सशक्तिकरण गतिविधियों में अपना योगदान दें।"
        path="/volunteer"
        breadcrumb={["हमारे साथ जुड़ें"]}
      />
      <PageHero
        overline={{ hi: "स्वयंसेवा", en: "Volunteer" }}
        title={{ hi: "हमसे जुड़ें – बदलाव का हिस्सा बनें", en: "Join Us – Be Part of the Change" }}
        description={{
          hi: "समाज में बदलाव केवल एक संस्था से नहीं, बल्कि लोगों की भागीदारी से संभव है। शिक्षा, स्वास्थ्य, पर्यावरण, महिला सशक्तिकरण और सामाजिक जागरूकता गतिविधियों में अपना योगदान दें।",
          en: "Change is possible through people's participation. Contribute to education, health, environment, women empowerment and social awareness activities.",
        }}
        testid="volunteer-page-heading"
      />

      <section className="py-20 bg-ngo-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Reveal>
            <form data-testid="volunteer-form" onSubmit={submit} noValidate className="bg-white rounded-3xl border border-ngo-deep/10 shadow-[0_20px_60px_rgba(23,107,58,0.10)] p-7 sm:p-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-11 h-11 rounded-xl bg-ngo-mint flex items-center justify-center text-ngo-deep">
                  <Users size={21} />
                </div>
                <h2 className="font-heading font-bold text-2xl text-ngo-ink">{pick({ hi: "स्वयंसेवक फॉर्म", en: "Volunteer Form" })}</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={label} htmlFor="v-name">{t("form.name")} *</label>
                  <input id="v-name" data-testid="volunteer-name" className={field} value={form.name} onChange={set("name")} />
                  {errors.name && <p className={err}>{errors.name}</p>}
                </div>
                <div>
                  <label className={label} htmlFor="v-phone">{t("form.phone")} *</label>
                  <input id="v-phone" data-testid="volunteer-phone" type="tel" className={field} value={form.phone} onChange={set("phone")} />
                  {errors.phone && <p className={err}>{errors.phone}</p>}
                </div>
                <div>
                  <label className={label} htmlFor="v-email">{t("form.email")}</label>
                  <input id="v-email" data-testid="volunteer-email" type="email" className={field} value={form.email} onChange={set("email")} />
                </div>
                <div>
                  <label className={label} htmlFor="v-city">{t("form.city")} *</label>
                  <input id="v-city" data-testid="volunteer-city" className={field} value={form.city} onChange={set("city")} />
                  {errors.city && <p className={err}>{errors.city}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className={label} htmlFor="v-interest">{t("form.interest")} *</label>
                  <select id="v-interest" data-testid="volunteer-interest" className={field} value={form.interest} onChange={set("interest")}>
                    <option value="">—</option>
                    {(INTERESTS[lang] || INTERESTS.hi).map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                  {errors.interest && <p className={err}>{errors.interest}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className={label} htmlFor="v-contribution">{t("form.contribution")} *</label>
                  <input id="v-contribution" data-testid="volunteer-contribution" className={field} value={form.contribution} onChange={set("contribution")} />
                  {errors.contribution && <p className={err}>{errors.contribution}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className={label} htmlFor="v-message">{t("form.message")}</label>
                  <textarea id="v-message" data-testid="volunteer-message" rows={4} className={field} value={form.message} onChange={set("message")} />
                </div>
              </div>
              <button
                type="submit"
                data-testid="volunteer-submit"
                disabled={busy}
                className="mt-8 w-full sm:w-auto px-10 py-4 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest hover:-translate-y-0.5 disabled:opacity-60 transition-[background-color,transform,opacity] duration-300"
              >
                {busy ? t("common.sending") : t("common.submit")}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
      <SpecialCta />
    </main>
  );
}
